import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, User, Building2, Phone, MessageSquare, Send, CheckCircle, Globe, Shield, Briefcase, HelpCircle, AlertCircle } from 'lucide-react';
import apiClient from '../../redux/authentication/utils/core/axios';
import PublicNav from '../../components/publicNav/PublicNav';

// contactType (frontend) -> ContactTopic (backend, POST /contact)
const TOPIC_MAP = { sales: 'sales', legal: 'legal', support: 'support', general: 'other' };

const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [contactType, setContactType] = useState('sales');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    companySize: '',
    useCase: '',
    subject: '',
    priority: 'normal',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Detect contact type from URL params or passed state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type') || location.state?.type || 'sales';
    setContactType(type);
  }, [location]);

  const contactTypes = {
    sales: {
      title: t('contact.types.sales.title'),
      subtitle: t('contact.types.sales.subtitle'),
      icon: Briefcase,
      color: 'purple',
      email: 'sales@stocker-beta.com'
    },
    legal: {
      title: t('contact.types.legal.title'),
      subtitle: t('contact.types.legal.subtitle'),
      icon: Shield,
      color: 'blue',
      email: 'legal@stocker-beta.com'
    },
    support: {
      title: t('contact.types.support.title'),
      subtitle: t('contact.types.support.subtitle'),
      icon: HelpCircle,
      color: 'green',
      email: 'support@stocker-beta.com'
    },
    general: {
      title: t('contact.types.general.title'),
      subtitle: t('contact.types.general.subtitle'),
      icon: Mail,
      color: 'indigo',
      email: 'contact@stocker-beta.com'
    }
  };

  const companySizes = t('contact.companySizes', { returnObjects: true });
  const salesUseCases = t('contact.salesUseCases', { returnObjects: true });
  const legalSubjects = t('contact.legalSubjects', { returnObjects: true });
  const supportPriorities = t('contact.supportPriorities', { returnObjects: true });
  const generalSubjects = t('contact.generalSubjects', { returnObjects: true });

  const config = contactTypes[contactType];
  const colorClasses = {
    purple: {
      gradient: 'from-purple-600 to-violet-600',
      hoverGradient: 'hover:from-purple-700 hover:to-violet-700',
      border: 'border-purple-300 dark:border-purple-500/20',
      bg: 'bg-purple-50 dark:bg-purple-500/20',
      text: 'text-purple-600 dark:text-purple-400',
      focus: 'focus:border-purple-500',
      ring: 'focus:ring-purple-500/50'
    },
    blue: {
      gradient: 'from-blue-600 to-cyan-600',
      hoverGradient: 'hover:from-blue-700 hover:to-cyan-700',
      border: 'border-blue-300 dark:border-blue-500/20',
      bg: 'bg-blue-50 dark:bg-blue-500/20',
      text: 'text-blue-600 dark:text-blue-400',
      focus: 'focus:border-blue-500',
      ring: 'focus:ring-blue-500/50'
    },
    green: {
      gradient: 'from-green-600 to-emerald-600',
      hoverGradient: 'hover:from-green-700 hover:to-emerald-700',
      border: 'border-green-300 dark:border-green-500/20',
      bg: 'bg-green-50 dark:bg-green-500/20',
      text: 'text-green-700 dark:text-green-400',
      focus: 'focus:border-green-500',
      ring: 'focus:ring-green-500/50'
    },
    indigo: {
      gradient: 'from-indigo-600 to-purple-600',
      hoverGradient: 'hover:from-indigo-700 hover:to-purple-700',
      border: 'border-indigo-300 dark:border-indigo-500/20',
      bg: 'bg-indigo-50 dark:bg-indigo-500/20',
      text: 'text-indigo-600 dark:text-indigo-400',
      focus: 'focus:border-indigo-500',
      ring: 'focus:ring-indigo-500/50'
    }
  };

  const colors = colorClasses[config.color];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Common validations
    if (!formData.firstName.trim()) newErrors.firstName = t('contact.errors.firstName');
    if (!formData.lastName.trim()) newErrors.lastName = t('contact.errors.lastName');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid');
    }

    // Type-specific validations
    if (contactType === 'sales') {
      if (!formData.company.trim()) newErrors.company = t('contact.errors.companyRequired');
      if (!formData.companySize) newErrors.companySize = t('contact.errors.companySizeRequired');
      if (!formData.useCase) newErrors.useCase = t('contact.errors.useCaseRequired');
    }

    if (contactType === 'legal') {
      if (!formData.company.trim()) newErrors.company = t('contact.errors.organizationRequired');
      if (!formData.subject) newErrors.subject = t('contact.errors.subjectRequired');
    }

    if (contactType === 'support') {
      if (!formData.subject) newErrors.subject = t('contact.errors.subjectRequiredSupport');
      if (!formData.priority) newErrors.priority = t('contact.errors.priorityRequired');
    }

    if (contactType === 'general') {
      if (!formData.subject) newErrors.subject = t('contact.errors.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = contactType === 'support'
        ? t('contact.errors.messageSupport')
        : t('contact.errors.messageDefault');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      document.getElementsByName(firstError)[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = contactType === 'sales'
        ? `Sales inquiry: ${formData.useCase || 'General'}`
        : formData.subject;

      await apiClient.post('/contact', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        subject,
        message: formData.message,
        company: formData.company || null,
        topic: TOPIC_MAP[contactType] || 'other',
        priority: contactType === 'support' ? formData.priority : 'normal',
        // Submitting this form is the explicit action the page's own
        // disclaimer text (below the form) describes as agreeing to the
        // Privacy Policy -- consent_privacy reflects that real action.
        consent_privacy: true,
      });

      setSubmitted(true);

      // Track with analytics
      if (window.gtag) {
        window.gtag('event', 'contact_form_submit', {
          contact_type: contactType,
          company: formData.company
        });
      }
    } catch (error) {
      console.warn('Submission error:', error);
      setErrors(prev => ({
        ...prev,
        submit: t('contact.errors.submitFailedPrefix') + ' ' + config.email
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    const Icon = config.icon;
    return (
      <div
        className="w-full min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-white dark:bg-[#09090f]"
        style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>
        <div
          style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: 900, height: 480,
            background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <div className="max-w-md w-full relative z-10">
          <div className="rounded-3xl p-12 text-center shadow-2xl bg-cream-900/[0.03] dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]">
            <div className="bg-green-100 dark:bg-green-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400">
              <CheckCircle className="text-green-700 dark:text-green-400" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('contact.submitted.title')}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {contactType === 'sales' && t('contact.submitted.sales')}
              {contactType === 'legal' && t('contact.submitted.legal')}
              {contactType === 'support' && t('contact.submitted.support')}
              {contactType === 'general' && t('contact.submitted.general')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              {t('contact.submitted.confirmationSentTo')} {formData.email}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/')}
                className={`bg-gradient-to-r ${colors.gradient} ${colors.hoverGradient} text-white px-8 py-3 rounded-lg font-semibold transition-all`}
              >
                {t('contact.submitted.backToHome')}
              </button>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    company: '',
                    jobTitle: '',
                    phone: '',
                    companySize: '',
                    useCase: '',
                    subject: '',
                    priority: 'normal',
                    message: ''
                  });
                }}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors text-sm"
              >
                {t('contact.submitted.submitAnother')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div
      className="w-full min-h-screen relative overflow-hidden bg-white dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <Helmet>
        <title>Contact | Stocker Beta</title>
        <meta name="description" content="Get in touch with the Stocker team." />
        <link rel="canonical" href="https://stocker-beta.com/contact" />
      </Helmet>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>
      <div
        style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Navigation (shared across every public page) */}
      <PublicNav />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Contact Type Selector */}
        <div className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(contactTypes).map(([key, type]) => {
              const TypeIcon = type.icon;
              const isActive = contactType === key;
              return (
                <button
                  key={key}
                  onClick={() => setContactType(key)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isActive
                      ? `border-${type.color}-500 ${colorClasses[type.color].bg}`
                      : 'border-gray-300 dark:border-gray-700/50 bg-cream-900/[0.03] dark:bg-white/5 hover:bg-cream-900/[0.06] dark:hover:bg-white/10'
                  }`}
                >
                  <TypeIcon className={`mx-auto mb-2 ${isActive ? colorClasses[type.color].text : 'text-gray-500 dark:text-gray-400'}`} size={24} />
                  <div className={`text-sm font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {t(`contact.typeShortLabel.${key}`)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colors.bg} border ${colors.border} mb-4`}>
            <Icon className={colors.text} size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {config.title}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {config.subtitle}
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          <div className={`bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border ${colors.border} rounded-2xl p-6 text-center`}>
            <div className={`${colors.bg} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Mail className={colors.text} size={24} />
            </div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('contact.infoCards.directEmail')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{config.email}</p>
          </div>

          <div className={`bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border ${colors.border} rounded-2xl p-6 text-center`}>
            <div className={`${colors.bg} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Globe className={colors.text} size={24} />
            </div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('contact.infoCards.euTeam')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('contact.infoCards.euTeamDesc')}</p>
          </div>

          <div className={`bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border ${colors.border} rounded-2xl p-6 text-center`}>
            <div className={`${colors.bg} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4`}>
              <CheckCircle className={colors.text} size={24} />
            </div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">
              {contactType === 'sales' ? t('contact.infoCards.responseTimeSales') : contactType === 'support' ? t('contact.infoCards.responseTimeSupport') : t('contact.infoCards.responseTimeDefault')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {contactType === 'sales' ? t('contact.infoCards.responseDescSales') : contactType === 'support' ? t('contact.infoCards.responseDescSupport') : t('contact.infoCards.responseDescDefault')}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className={`bg-cream-900/[0.03] dark:bg-white/10 backdrop-blur-xl border ${colors.border} rounded-3xl p-6 md:p-12`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.firstName')}
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                    }`}
                    placeholder={t('contact.form.firstNamePlaceholder')}
                  />
                </div>
                {errors.firstName && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.lastName')}
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                    }`}
                    placeholder={t('contact.form.lastNamePlaceholder')}
                  />
                </div>
                {errors.lastName && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Email & Company */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {contactType === 'sales' ? t('contact.form.emailSales') : t('contact.form.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                    }`}
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.email}
                  </p>
                )}
              </div>

              {(contactType === 'sales' || contactType === 'legal') && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {contactType === 'legal' ? t('contact.form.companyLegal') : t('contact.form.company')}
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors ${
                        errors.company ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                      }`}
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>
                  {errors.company && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.company}
                    </p>
                  )}
                </div>
              )}

              {contactType === 'support' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.companyOptional')}
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 border-gray-300 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors`}
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>
                </div>
              )}

              {contactType === 'general' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.phoneOptional')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 border-gray-300 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors`}
                      placeholder={t('contact.form.phonePlaceholder')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Job Title & Phone (Sales) */}
            {contactType === 'sales' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.jobTitle')}
                  </label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 border-gray-300 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors`}
                    placeholder={t('contact.form.jobTitlePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.phoneNumber')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 border-gray-300 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors`}
                      placeholder={t('contact.form.phonePlaceholder')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Company Size (Sales) */}
            {contactType === 'sales' && (
              <div>
                <label htmlFor="companySize" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.companySize')}
                </label>
                <select
                  id="companySize"
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white focus:outline-none ${colors.focus} transition-colors ${
                    errors.companySize ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                  }`}
                >
                  <option value="">{t('contact.form.companySizeSelect')}</option>
                  {companySizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                {errors.companySize && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.companySize}
                  </p>
                )}
              </div>
            )}

            {/* Use Case (Sales) */}
            {contactType === 'sales' && (
              <div>
                <label htmlFor="useCase" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.useCase')}
                </label>
                <select
                  id="useCase"
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white focus:outline-none ${colors.focus} transition-colors ${
                    errors.useCase ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                  }`}
                >
                  <option value="">{t('contact.form.useCaseSelect')}</option>
                  {salesUseCases.map(useCase => (
                    <option key={useCase} value={useCase}>{useCase}</option>
                  ))}
                </select>
                {errors.useCase && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.useCase}
                  </p>
                )}
              </div>
            )}

            {/* Subject (Legal) */}
            {contactType === 'legal' && (
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.legalSubject')}
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white focus:outline-none ${colors.focus} transition-colors ${
                    errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                  }`}
                >
                  <option value="">{t('contact.form.legalSubjectSelect')}</option>
                  {legalSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                {errors.subject && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.subject}
                  </p>
                )}
              </div>
            )}

            {/* Subject & Priority (Support) */}
            {contactType === 'support' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.issueType')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors ${
                      errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                    }`}
                    placeholder={t('contact.form.issueTypePlaceholder')}
                  />
                  {errors.subject && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {t('contact.form.priority')}
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white focus:outline-none ${colors.focus} transition-colors ${
                      errors.priority ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                    }`}
                  >
                    {supportPriorities.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  {errors.priority && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.priority}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Subject (General) */}
            {contactType === 'general' && (
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('contact.form.generalSubject')}
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white focus:outline-none ${colors.focus} transition-colors ${
                    errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                  }`}
                >
                  <option value="">{t('contact.form.generalSubjectSelect')}</option>
                  {generalSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                {errors.subject && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.subject}
                  </p>
                )}
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                {contactType === 'support' ? t('contact.form.messageSupport') :
                 contactType === 'legal' ? t('contact.form.messageLegal') :
                 contactType === 'sales' ? t('contact.form.messageSales') :
                 t('contact.form.messageGeneral')}
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" size={20} />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={contactType === 'support' ? 6 : 5}
                  className={`w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-gray-800/50 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none ${colors.focus} transition-colors resize-none ${
                    errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-700/50'
                  }`}
                  placeholder={
                    contactType === 'support'
                      ? t('contact.form.messagePlaceholderSupport')
                      : contactType === 'legal'
                      ? t('contact.form.messagePlaceholderLegal')
                      : contactType === 'sales'
                      ? t('contact.form.messagePlaceholderSales')
                      : t('contact.form.messagePlaceholderGeneral')
                  }
                />
              </div>
              {errors.message && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.message}
                </p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertCircle className="flex-shrink-0 mt-0.5" size={16} />
                <span>{errors.submit}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 bg-gradient-to-r ${colors.gradient} ${colors.hoverGradient} text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-4 ${colors.ring} disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg flex items-center justify-center gap-2`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  {t('contact.form.submitting')}
                </>
              ) : (
                <>
                  <Send size={20} />
                  {contactType === 'support' ? t('contact.form.submitTicket') : t('contact.form.submitInquiry')}
                </>
              )}
            </button>

            {/* Helper Text */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {contactType === 'support' && t('contact.helperText.support')}
              {contactType === 'legal' && t('contact.helperText.legal')}
              {contactType === 'sales' && `${t('contact.helperText.salesPrefix')} ${config.email}`}
              {contactType === 'general' && `${t('contact.helperText.generalPrefix')} ${config.email}`}
            </p>
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-8 md:mt-12 text-center space-y-4">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {t('contact.agreement.prefix')}{' '}
            <button
              onClick={() => navigate('/privacy')}
              className={`${colors.text} hover:underline`}
            >
              {t('contact.agreement.privacy')}
            </button>
            {' '}{t('contact.agreement.and')}{' '}
            <button
              onClick={() => navigate('/terms')}
              className={`${colors.text} hover:underline`}
            >
              {t('contact.agreement.terms')}
            </button>
          </p>

          {/* Alternative Contact Methods */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700/30">
            <p className="text-gray-500 text-sm mb-3">{t('contact.lookingForSomethingElse')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(contactTypes)
                .filter(([key]) => key !== contactType)
                .map(([key, type]) => (
                  <button
                    key={key}
                    onClick={() => setContactType(key)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors underline"
                  >
                    {type.title}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;